import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Paper, Link,
} from '@mui/material';
import { signup } from '../api/auth';
import { useAuth } from '../contexts/AuthContext';
import { roleHome } from '../lib/roleHome';
import { GoogleAuthButton } from '../components/auth/GoogleAuthButton';

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

export const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();

  const handleGoogleSuccess = async (idToken: string) => {
    try {
      const u = await loginWithGoogle(idToken);
      navigate(roleHome(u.role), { replace: true });
    } catch {
      // Silent for now — Google failure on Signup is rare.
    }
  };

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => signup({ email, password, fullName }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  if (mutation.isSuccess) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: { xs: 4, sm: 8 }, px: 3 }}>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="h3" sx={{ color: NAVY, mb: 2 }}>Revisa tu correo</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 3 }}>
            Te enviamos un enlace para verificar tu cuenta a <strong>{email}</strong>. El enlace expira en 24 horas.
          </Typography>
          <Link component={RouterLink} to="/login" sx={{ color: BLUE }}>Volver al inicio de sesión</Link>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto', mt: { xs: 4, sm: 8 }, px: 3 }}>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 3, sm: 4 } }}>
        <GoogleAuthButton onSuccess={handleGoogleSuccess} />
        <Typography variant="h3" sx={{ color: NAVY, mb: 1 }}>Crea tu cuenta</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          ¿Ya tienes cuenta? <Link component={RouterLink} to="/login" sx={{ color: BLUE }}>Iniciar sesión</Link>
        </Typography>

        {mutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(mutation.error as any)?.response?.data?.message ?? 'Error al crear la cuenta.'}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth label="Nombre completo" value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required sx={{ mb: 2 }} inputProps={{ minLength: 2 }}
          />
          <TextField
            fullWidth type="email" label="Correo" value={email}
            onChange={(e) => setEmail(e.target.value)}
            required sx={{ mb: 2 }}
          />
          <TextField
            fullWidth type="password" label="Contraseña" value={password}
            onChange={(e) => setPassword(e.target.value)}
            required sx={{ mb: 3 }} inputProps={{ minLength: 8 }}
            helperText="Mínimo 8 caracteres"
          />
          <Button
            type="submit" fullWidth variant="contained" size="large"
            disabled={mutation.isPending}
            sx={{ bgcolor: BLUE, py: 1.5, '&:hover': { bgcolor: '#0052a3' } }}>
            {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Registrarse'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};
